import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Loader2, Clock, ShieldCheck, BadgeCheck, Upload, Globe, CheckCircle2, ArrowRight } from "lucide-react";

export function VerificationCard() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [applyFor, setApplyFor] = useState(null); // "seller" | "business" | null
  const lastCat = useRef(null);
  if (applyFor) lastCat.current = applyFor;
  const load = () => api.get("/verification/me").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const cats = [
    { key: "seller", label: t("lc_verified_seller"), verified: data.verified_seller, req: data.requests?.seller, amber: false, expires: data.verified_seller_expires,
      blurb: "Confirm your identity with a government ID so buyers know they're dealing with a real person." },
    { key: "business", label: t("lc_verified_business"), verified: data.verified_business, req: data.requests?.business, amber: true, expires: data.verified_business_expires,
      blurb: "Verify your business, breeder or shelter with official documentation to earn a business badge." },
    { key: "breeder", label: t("lc_verified_breeder"), verified: data.verified_breeder, req: data.requests?.breeder, purple: true, expires: data.verified_breeder_expires,
      blurb: "Show you're a responsible breeder (e.g. club registration or vet reference) and list the species you breed on your profile." },
  ];

  const expiryNote = (iso) => {
    if (!iso) return <span className="text-slate-400">· never expires</span>;
    const days = Math.ceil((new Date(iso) - Date.now()) / 86400000);
    if (days <= 0) return null;
    const soon = days <= 14;
    return <span className={soon ? "text-amber-600 font-semibold" : "text-slate-400"}>· {soon ? `expires in ${days}d — re-verify soon` : `valid until ${new Date(iso).toLocaleDateString()}`}</span>;
  };

  return (
    <div className="max-w-xl bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="verification-card">
      <div className="flex items-start gap-4 mb-4">
        <span className="h-10 w-10 rounded-xl bg-[#FFF4F0] flex items-center justify-center shrink-0"><ShieldCheck size={20} className="text-[#F5551E]" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-[#12234A]">{t("d_verify_title")}</p>
          <p className="text-sm text-slate-500 mt-1">Apply for any badge — or all three. Each category is reviewed separately.</p>
          <Link to="/get-verified" data-testid="dashboard-get-verified-link" className="text-sm font-semibold text-[#F5551E] hover:underline mt-1.5 inline-flex items-center gap-1">See what you unlock <ArrowRight size={14} /></Link>
        </div>
      </div>
      <div className="space-y-3">
        {cats.map((c) => {
          const status = c.verified ? "verified" : (c.req?.status === "pending" ? "pending" : c.req?.status === "rejected" ? "rejected" : "none");
          return (
            <div key={c.key} className="rounded-xl border border-slate-200 p-4 flex items-start justify-between gap-3" data-testid={`verif-row-${c.key}`}>
              <div className="min-w-0">
                <p className="font-semibold text-[#12234A] flex items-center gap-1.5">
                  <BadgeCheck size={15} className={c.purple ? "text-purple-600" : c.amber ? "text-amber-500" : "text-emerald-600"} /> {c.label}
                </p>
                {status === "verified" ? (
                  <p className="text-sm text-emerald-600 font-semibold mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`verif-status-${c.key}`}><BadgeCheck size={15} /> {t("d_verify_verified_cat").replace("{badge}", c.label)} <span className="text-xs font-normal">{expiryNote(c.expires)}</span></p>
                ) : status === "pending" ? (
                  <p className="text-sm text-amber-600 font-semibold mt-1 flex items-center gap-1.5" data-testid={`verif-status-${c.key}`}><Clock size={15} /> {t("d_verify_pending")}</p>
                ) : status === "rejected" ? (
                  <div data-testid={`verif-status-${c.key}`}>
                    <p className="text-sm text-red-600 font-semibold mt-1 flex items-center gap-1.5"><Clock size={15} /> {t("d_verify_rejected")}</p>
                    {c.req?.note && <p className="text-xs text-slate-500 mt-1">{t("d_verify_reason")} {c.req.note}</p>}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">{c.blurb}</p>
                )}
              </div>
              {status !== "verified" && status !== "pending" && (
                <Button onClick={() => setApplyFor(c.key)} data-testid={`verif-apply-${c.key}`} size="sm"
                  className={`rounded-xl text-white gap-1.5 shrink-0 ${c.purple ? "bg-purple-600 hover:bg-purple-700" : c.amber ? "bg-amber-500 hover:bg-amber-600" : "bg-[#F5551E] hover:bg-[#E04812]"}`}>
                  <ShieldCheck size={15} /> {status === "rejected" ? t("d_verify_new_request") : t("d_verify_request")}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <VerificationDialog open={!!applyFor} category={applyFor || lastCat.current} onClose={() => setApplyFor(null)} onDone={() => { setApplyFor(null); load(); }} />
    </div>
  );
}

export function VerificationDialog({ open, category, onClose, onDone }) {
  const { t } = useLanguage();
  const isBusiness = category === "business" || category === "breeder";
  const [form, setForm] = useState({ full_name: "", business_name: "", website: "", document: "" });
  const [docName, setDocName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) { setForm({ full_name: "", business_name: "", website: "", document: "" }); setDocName(""); } }, [open, category]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("File must be under 8MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("document", data.path); setDocName(file.name);
      toast.success("Document uploaded");
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!form.document) return toast.error("Please upload a supporting document");
    if (isBusiness && !form.business_name.trim()) return toast.error("Business/operation name is required");
    if (!isBusiness && !form.full_name.trim()) return toast.error("Your full legal name is required");
    setSubmitting(true);
    try {
      await api.post("/verification/request", { category, ...form });
      toast.success("Verification request submitted!");
      onDone();
    } catch (e) { toast.error(errText(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="verification-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><ShieldCheck size={18} className="text-[#F5551E]" /> {category === "breeder" ? t("lc_verified_breeder") : isBusiness ? t("lc_verified_business") : t("lc_verified_seller")}</DialogTitle>
          <DialogDescription className="text-slate-500">
            {category === "breeder" ? t("d_verify_dlg_desc_breeder") : isBusiness ? t("d_verify_dlg_desc_biz") : t("d_verify_dlg_desc_ind")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isBusiness ? (
            <>
              <div>
                <Label>{t("d_verify_biz_name")}</Label>
                <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="verify-business-name" className="mt-1 h-11 rounded-xl" placeholder="e.g. Maple Paws Shelter" />
              </div>
              <div>
                <Label>{t("d_verify_website")}</Label>
                <div className="relative mt-1">
                  <Globe size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="verify-website" className="pl-9 h-11 rounded-xl" placeholder="https://…" />
                </div>
              </div>
            </>
          ) : (
            <div>
              <Label>{t("d_verify_full_name_label")}</Label>
              <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="verify-full-name" className="mt-1 h-11 rounded-xl" placeholder={t("d_verify_full_name_ph")} />
            </div>
          )}
          <div>
            <Label>{category === "breeder" ? t("d_verify_doc_breeder") : isBusiness ? t("d_verify_doc_biz") : t("d_verify_doc_id")}</Label>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="verify-upload-btn"
              className="mt-1 w-full h-11 rounded-xl border border-dashed border-slate-300 hover:border-[#F5551E] text-slate-500 hover:text-[#F5551E] flex items-center justify-center gap-2 transition-colors">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : (form.document ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Upload size={16} />)}
              {form.document ? (docName || t("d_verify_doc_uploaded")) : t("d_verify_upload")}
            </button>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onPick} data-testid="verify-file-input" className="hidden" />
            <p className="text-xs text-slate-400 mt-1">{t("d_verify_doc_note")}</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} className="rounded-xl">{t("d_cancel")}</Button>
          <Button onClick={submit} disabled={submitting || uploading} data-testid="verify-submit" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5">
            {submitting ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} {t("d_verify_submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
